import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { DataGrid, GridColDef, GridSelectionModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import {
	IAdminDataset,
	bulkAddDatasetPermissions,
	useAdminDatasetsQuery,
	useAdminUserDatasetPermissionsQuery,
} from '../../api/admin';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';

interface Props {
	open: boolean;
	onClose: () => void;
	userId: number;
	userEmail: string;
}

const DATASET_COLUMNS: GridColDef[] = [
	{ field: 'vendor_name', headerName: 'Vendor', flex: 1, minWidth: 120 },
	{ field: 'name', headerName: 'Dataset', flex: 1.2, minWidth: 140 },
	{
		field: 'internal_description',
		headerName: 'Description',
		flex: 2,
		minWidth: 180,
	},
	{
		field: 'is_platform_included',
		headerName: 'Platform',
		width: 90,
		type: 'boolean',
	},
];

export default function AddDatasetPermissionDialog({ open, onClose, userId, userEmail }: Props) {
	const queryClient = useQueryClient();
	const { data: datasets, isLoading: datasetsLoading } = useAdminDatasetsQuery();
	const { data: existingPermissions } = useAdminUserDatasetPermissionsQuery(userId);

	const grantedDatasetIds = new Set((existingPermissions ?? []).map((p) => p.dataset_id));
	const availableDatasets = (datasets ?? []).filter((ds) => !grantedDatasetIds.has(ds.id));

	const [step, setStep] = useState<'select' | 'confirm'>('select');
	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
	const [accessFrom] = useState<string>(
		new Date().toISOString().split('T')[0]
	);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const selected: IAdminDataset[] = availableDatasets.filter((ds) =>
		selectionModel.includes(ds.id)
	);

	function resetAndClose() {
		setStep('select');
		setSelectionModel([]);
		setError(null);
		onClose();
	}

	async function handleConfirm() {
		setSubmitting(true);
		setError(null);
		try {
			const granted = await bulkAddDatasetPermissions(
				selected.map((ds) => ds.id),
				[userId],
				accessFrom
			);
			await queryClient.invalidateQueries(['admin-user-dataset-permissions', userId]);
			if (granted.length < selected.length) {
				setError(
					`Granted ${granted.length} of ${selected.length} datasets. The skipped ones and the reason are in the admin notification email.`
				);
				return;
			}
			resetAndClose();
		} catch (e: any) {
			setError(
				e?.response?.data?.detail ?? 'An unexpected error occurred. Please try again.'
			);
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog open={open} onClose={resetAndClose} maxWidth="lg" fullWidth PaperProps={{ sx: { backgroundImage: 'none' } }}>
			{step === 'select' ? (
				<>
					<DialogTitle>Add Dataset Permissions</DialogTitle>
					<DialogContent sx={{ pb: 1 }}>
						<Box sx={{ height: 420, width: '100%', mt: 1 }}>
							<DataGrid
								rows={availableDatasets}
								columns={DATASET_COLUMNS}
								loading={datasetsLoading}
								density="compact"
								checkboxSelection
								disableSelectionOnClick
								selectionModel={selectionModel}
								onSelectionModelChange={setSelectionModel}
								components={{ Toolbar: AdminQuickFilterToolbar }}
								componentsProps={{ toolbar: { showQuickFilter: true } }}
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={resetAndClose}>Cancel</Button>
						<Button
							variant="contained"
							onClick={() => setStep('confirm')}
							disabled={selected.length === 0}
						>
							Next ({selected.length} selected)
						</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogTitle>Confirm Permission Grant</DialogTitle>
					<DialogContent>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						<Typography gutterBottom>
							Grant access to the following dataset
							{selected.length > 1 ? 's' : ''} to{' '}
							<strong>{userEmail}</strong>?
						</Typography>
						<Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
							{selected.map((ds) => (
								<li key={ds.id}>
									<strong>{ds.vendor_name}</strong> — {ds.name}
								</li>
							))}
						</Box>
						<Typography variant="body2" color="text.secondary">
							Access from: {new Date(accessFrom).toLocaleDateString('en-CA')} (today)
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setStep('select')} disabled={submitting}>
							Back
						</Button>
						<Button
							variant="contained"
							color="success"
							onClick={handleConfirm}
							disabled={submitting}
							startIcon={submitting ? <CircularProgress size={16} /> : undefined}
						>
							{submitting ? 'Granting…' : 'Confirm Grant'}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
